import { DOMEvents, DOMUtils } from '../../../../utils/dom'

import { IAdsConfiguration, ICommentAdConfiguration } from '../../../../domain/conf/ads'
import { Utils } from '../../../../utils/common'
import { logger } from '../../../../utils/common/logger'
import { Store } from '../../../../utils/storage/keys'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { IViafouraWidget, ViafouraWidget } from './widget'

interface IWindow extends Window {
    vf: any
    vfQ: any
}

declare let window: IWindow

export class Viafoura {
    public adLight: boolean
    public commentWidget: IViafouraWidget
    public carouselWidget: IViafouraWidget

    public displayAds = true
    public loadViafouraJS = false

    private DOM = {
        attrs: {
            jsUrl: '//cdn.viafoura.net/vf-v2.js'
        },
        classes: {
            adLight: 'ad__adlight'
        },
        widgets: {
            contentRecirculation: 'vf-content-recirculation',
            conversations: 'vf-conversations',
            dropdownItem: 'vf-dropdown__list-item',
            trayTrigger: 'vf-tray-trigger'
        },
        wrappers: {
            carousel: {
                selector: 'data-sso-carousel',
                title: 'Most Commented'
            },
            commenting: {
                attrs: {
                    commentsLimit: '',
                    firstPromoPosition: '1',
                    initialHeight: '',
                    promoInterval: '10',
                    replyLimit: ''
                },
                selector: 'data-sso-commenting',
                title: ''
            }
        }
    }

    private viafouraClickListeners: { [key: string]: (...args: any[]) => void } = {
        'trending-list__title': this.displayConsole,
        'vf-js-tray-activator': this.displayConsole,
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: social login provider name, or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            sign_in_method: 'sign_in_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signin,
                integration_name: 'Viafoura',
                sign_in_method: signInMethod
            }
        }))
    }

    /**
     * Send registration analytics event
     * @param {string} registrationMethod: registration provider name, or 'traditionalSignin'
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            registration_method: 'registration_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.registration,
                integration_name: 'Viafoura',
                registration_method: registrationMethod
            }
        }))
    }

    /**
     * get vf comment text from localstorage, from before redirect to sign-in page
     * if it exists, populate the textarea with the value
     */
    public static async PopulateCommentSection(): Promise<void> {
        const vfCommentText = localStorage.getItem(Store.VF_COMMENT_TEXT)
        if (vfCommentText) {
            logger('debug', 'populating viafoura comment textarea with: ' + vfCommentText)
            // tslint:disable-next-line: max-line-length
            const vfCommentTextArea = document.querySelector('.vf-content-editor-input') as HTMLTextAreaElement
            if (vfCommentTextArea) {
                vfCommentTextArea.value = vfCommentText
                logger('debug', 'populated vf comment textarea successfully')
            } else {
                logger('debug', 'unable to populate vf comment textarea, cannot find element')
            }
            // remove the value from localstorage
            localStorage.removeItem(Store.VF_COMMENT_TEXT)
        }
    }

    public static ScrollToComments() {
        // if query param "comments" exists in URL, scroll to section with id "comments"
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.has('comments')) {
            const commentsSection = document.getElementById('comments')
            if (commentsSection) {
                commentsSection.scrollIntoView()
            }
        }
    }

    public enable() {
        this.addSubscriptions()
        this.addListeners()
    }

    public addSubscriptions() {
        window.vfQ.push(() => {
            this.buildSubscriptions()
        })
    }

    public buildSubscriptions() {
        if (this.commentWidget && this.displayAds) {
            // subscribe to the viafoura event fired when the ad slot is available in the comment widget
            window.vf.$subscribe(
                'vf-ads',
                'requestCommentAd',
                // bind the correct scope to handle the ad slot for the widget
                this.commentWidget.handleAdSlotEvent.bind(this.commentWidget)
            )
        }
        if (this.carouselWidget && this.displayAds) {
            // subscribe to the viafoura event fired when the ad slot is available in the carousel widget
            window.vf.$subscribe(
                'vf-ads',
                'requestContentRecirculationAd',
                // bind the correct scope to handle the ad slot for the widget
                this.carouselWidget.handleAdSlotEvent.bind(this.carouselWidget)
            )
        }

        window.vf.$subscribe('logout', 'success', () => {
            document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGOUT))
        })

        window.vf.$prepublish((channel: string, event: string, ...args: object[]) => {
            if (channel === 'authentication' && event === 'required') {
                // get value of textarea with class vf-content-editor-input and store it in localstorage
                const textarea = document.querySelector<HTMLTextAreaElement>('.vf-content-editor-input')
                if (textarea) {
                    localStorage.setItem(Store.VF_COMMENT_TEXT, textarea.value)
                }
                document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGIN, {commentRedirect: true}))
                return false
            }

            return {channel, event, args}
        })

    }

    public addListeners() {
        document.addEventListener('click', async (e: Event) => {
            const el = e.target as Element
            for (const target in this.viafouraClickListeners) {
                if (el.classList.contains(target)) {
                    this.eventFunction(this.viafouraClickListeners[target], target)
                }
            }

        })
        const commentsSection = document.querySelector('.comments__section-viafoura-container') as HTMLElement
        if (commentsSection) {
            commentsSection.addEventListener('click', async () => {
                logger('debug', 'Viafoura clicked - populating comments section')
                await Viafoura.PopulateCommentSection()
            })
        }

    }

    /**
     * Remove ads from any registered widgets
     */
    public removeCommentWidgetsAdUI() {
        this.displayAds = false
        for (const widget of Object.values(this.DOM.wrappers)) {
            this.updateAdlightUserStatus(widget, this.displayAds)
            logger('debug', 'Viafoura set adLight status for: ' + widget)
        }
    }

    /**
     * Update adLight class to widget container if valid adLight user
     * @param widget
     * @param displayAds
     */
    public updateAdlightUserStatus(widget: any, displayAds: boolean) {
        const elArr = document.querySelectorAll<HTMLInputElement>(`[${widget.selector}]`)

        if (elArr && !displayAds) {
            for (const el of elArr) {
                DOMUtils.addClass(el, this.DOM.classes.adLight)
                logger('debug', `viafoura remove ads from: ${el}`)
            }
        } else if (elArr && displayAds) {
            for (const el of elArr) {
                DOMUtils.removeClass(el, this.DOM.classes.adLight)
            }
        }
    }

    public displayConsole(target: string) {
        logger('debug', 'target: ', target)
    }

    public eventFunction(event: any, target: string) {
        event(target)
    }

    /**
     * Initialize commenting features
     *
     * @param conf
     * @param accessToken  - normally empty. populated with Login Radius token if present
     * @returns
     */
    public async initialize(conf?: IAdsConfiguration, accessToken?: string) {
        if (!!window.vfQ) {
            return this
        }

        window.vf = window.vf || {events: {}}
        window.vfQ = window.vfQ || []

        let commentAdConfiguration: ICommentAdConfiguration

        if (conf) {
            commentAdConfiguration = conf.commentAdConfiguration
        }

        if (commentAdConfiguration) {
            this.DOM.wrappers.commenting.attrs.firstPromoPosition = commentAdConfiguration.firstPromoPosition ?
                commentAdConfiguration.firstPromoPosition : '1'
            this.DOM.wrappers.commenting.attrs.promoInterval = commentAdConfiguration.promoInterval ?
                commentAdConfiguration.promoInterval : '10'
        }

        logger('debug', 'comment section is now in view')

        // get load required status
        await this.validateViafouraRequired(this.DOM.wrappers)
        if (this.loadViafouraJS) {
            for (const [type, widget] of Object.entries(this.DOM.wrappers)) {
                this.updateAdlightUserStatus(widget, this.displayAds)
                await this.loadViafouraUI(type, widget)
                if (accessToken) {
                    window.vfQ.push(() => {
                        logger('debug', 'logging viafoura user in using token from FEM')
                        window.vf.session.login.loginRadius(accessToken)

                    })
                }
                logger('debug', 'Viafoura UI is now ready for: ' + widget)
            }
            // load viafoura scss
            await this.scssLoad()
            logger('debug', 'Viafoura CSS is now ready')
            // load viafoura JS
            await this.scriptLoad(this.DOM.attrs.jsUrl)

            window.vfQ.push(async () => {
                logger('debug', 'Viafoura JS is now ready')
                this.enable()
                // do widget signout if vf signout flag is found in localstorage
                if (localStorage && localStorage.getItem(Store.VF_LOGOUT)) {
                    await this.doWidgetSignout()
                    localStorage.removeItem(Store.VF_LOGOUT)
                }
                await DOMUtils.ready
                Viafoura.ScrollToComments()
            })
        }

        return this
    }

    /**
     * Signout viafoura if using login radius tray integration
     */
    public async doWidgetSignout() {
        if (window.vf && window.vf.session) {
            logger('info', 'signing out viafoura user')
            await window.vf.session.logout()
            logger('info', 'viafoura user signed out')
        } else if (window.localStorage) {
            logger('debug', 'Viafoura object or session not found in window, setting localstorage flag')
            // set a logout flag in localstorage
            localStorage.setItem(Store.VF_LOGOUT, 'true')
        }
    }

    /**
     * Validate a viafoura wrapper is present in DOM (vf-v2.js is required)
     */
    public async validateViafouraRequired(wrappers: object) {
        await DOMUtils.ready // wait for DOM
        // await this.hasSession() // wait for session
        // Cycle through supported wrapper object
        for (const attrs of Object.values(wrappers)) {
            // Search for supported wrapper selector in dom
            if (document.querySelector(`[${attrs.selector}]`)) {
                // If selector is found then viafoura is set to required
                this.loadViafouraJS = true
                return true
            }
        }
        return false
    }

    /**
     * Load viafoura css file from scssUrl
     */
    public async scssLoad(): Promise<boolean> {
        try {
            logger('debug', `viafoura loading css './styles/viafoura.scss'`)
            const scss = import('./styles/viafoura.scss')
            DOMUtils.addStyles((await scss).default)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load viafoura js file from jsUrl
     */
    public async scriptLoad(jsUrl: string): Promise<boolean> {
        try {
            logger('debug', `viafoura loading script ${this.DOM.attrs.jsUrl}`)
            DOMUtils.loadScript(jsUrl)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load the available widgets as objects.  The ViafouraWidget class takes
     * care of the interaction with the ad slot
     */
    public async loadViafouraUI(type: string, widget: any) {
        try {
            // Get and set the element by selector
            const el = document.querySelector<HTMLInputElement>(`[${widget.selector}]`)
            if (el) {

                if (type === 'commenting') {
                    logger('debug', 'viafoura creating commenting widget')

                    // Get initial properties of wrapper and store in object
                    widget.attrs.initialHeight = el.dataset.initialHeight
                    widget.attrs.replyLimit = el.dataset.replyLimit
                    widget.attrs.commentsLimit = el.dataset.commentsLimit

                    // BT Issue -
                    // Create commenting widget
                    this.commentWidget = new ViafouraWidget(
                        this.DOM.wrappers.commenting.selector,
                        [
                            {
                                key: 'first-promo-position',
                                value: widget.attrs.firstPromoPosition
                            },
                            {
                                key: 'promoInterval',
                                value: widget.attrs.promoInterval
                            },
                            {
                                key: 'initial-height',
                                value: widget.attrs.initialHeight
                            },
                            {
                                key: 'limit',
                                value: widget.attrs.commentsLimit
                            },
                            {
                                key: 'reply-limit',
                                value: widget.attrs.replyLimit
                            }
                        ],
                        this.DOM.widgets.conversations,
                        [`${this.DOM.widgets.trayTrigger}`],
                        2 // tslint:disable-line: Profile: no-magic-numbers
                    )
                    return true
                } else if (type === 'carousel') {
                    logger('debug', 'viafoura creating carousel widget')

                    // Create carousel widget
                    this.carouselWidget = new ViafouraWidget(
                        this.DOM.wrappers.carousel.selector,
                        [
                            {
                                key: 'title',
                                value: widget.title
                            }
                        ],
                        this.DOM.widgets.contentRecirculation
                    )
                    return true
                }

            }
            logger('debug', `viafoura widget selector not found: ${widget.selector}`)
            return false

        } catch (err) {
            logger('error', `viafoura create ${widget.selector} error: ${err}`)
            return false
        }
    }
}
